//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CatalogSelectionModalActions } from '@catalog-ingestion/actions/catalogSelectionModalActions';
import CatalogSelectionModal from '@catalog-ingestion/components/modals/catalog-selection-modal/CatalogSelectionModal';
import adobeIcon from '@images/adobe-catalog.png';
import shopifyIcon from '@images/shopify.png';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { recordEvent } from '@tracker/tracker';
import { TRACKING_ACTION, TRACKING_CATEGORY } from '@tracker/trackerModel';
import { OnboardingStepCard } from '@welcome-page/components/onboarding/steps/OnboardingStepCard';
import { OnboardingState } from '@welcome-page/components/onboarding/steps/OnboardingStepCardGrid';
import {
  ONBOARDING_STEP_ICON_SIZE,
  OnboardingCatalogType,
} from '@welcome-page/constants/OnboardingConstants';
import { getIsEcommerceIntegrationsMagentoEnabled } from '@welcome-page/selectors/getIsEcommerceIntegrationsMagentoEnabled';
import { getIsEcommerceIntegrationsShopifyEnabled } from '@welcome-page/selectors/getIsEcommerceIntegrationsShopifyEnabled';

export const OnboardingCatalogCard = ({ isError, status }: OnboardingState) => {
  const dispatch = useDispatch();

  const isShopifyIntegrationEnabled = useSelector(
    getIsEcommerceIntegrationsShopifyEnabled,
  );

  const isMagentoIntegrationEnabled = useSelector(
    getIsEcommerceIntegrationsMagentoEnabled,
  );

  const handleCatalogConnection = () => {
    switch (catalogType) {
      case OnboardingCatalogType.GOOGLE:
        dispatch(CatalogSelectionModalActions.openCatalogSelectionModal());
        return;
      case OnboardingCatalogType.SHOPIFY:
      case OnboardingCatalogType.MAGENTO:
        recordEvent(
          TRACKING_CATEGORY.SHOPIFY_ONBOARDING,
          TRACKING_ACTION.CONNECT_ACCOUNT,
          catalogType === OnboardingCatalogType.SHOPIFY ? 'Shopify' : 'Magento',
        );
        // As the ecommerce-setup is currently an old angular code and we are not able to invalidate
        // the react queries from there, the reload option will ensure that the onboarding catalog
        // status will be fetched from the API when the user is back to the onboarding screen.
        navigateToState('account-settings.ecommerceSetup', undefined, {
          reload: true,
        });
        return;
    }
  };

  const catalogType = (() => {
    if (isShopifyIntegrationEnabled) {
      return OnboardingCatalogType.SHOPIFY;
    } else if (isMagentoIntegrationEnabled) {
      return OnboardingCatalogType.MAGENTO;
    }
    return OnboardingCatalogType.GOOGLE;
  })();

  return (
    <>
      <OnboardingStepCard
        description='Connect your product catalog to tag products on content.'
        icons={[
          <img
            key='shopify-logo'
            height={ONBOARDING_STEP_ICON_SIZE}
            src={shopifyIcon}
            width={ONBOARDING_STEP_ICON_SIZE}
          />,
          <img
            key='adobe-logo'
            height={ONBOARDING_STEP_ICON_SIZE}
            src={adobeIcon}
            width='27px'
          />,
        ]}
        onClickConnect={handleCatalogConnection}
        onClickSettings={() =>
          dispatch(CatalogSelectionModalActions.openCatalogSelectionModal())
        }
        pendingMessage="Your product catalog is processing - we'll email you when it's ready to view!"
        showAlarmTooltip={isError}
        status={status}
        title='Catalog'
      />
      <CatalogSelectionModal />
    </>
  );
};
